import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Upload } from 'lucide-react';

import { selectIsAdmin, selectUserToken } from '../redux/features/user/userSelectors';

import { useGetParserDataQuery, useGetSpidersQuery } from '../redux/services/parser/parserApi';
import Multiselect from 'multiselect-react-dropdown';

import { Container } from '../layouts/Container';
import { PaginationComponent } from '../components/ui/pagination/PaginationComponent';
import { ParserStats } from '../components/ui/ParserStats';
import { ButtonTemplate } from '../components/ui/buttons/ButtonTemplate';
import { ImportFlow } from '../features/parser-import/ImportFlow';
import { useGetAvailableCountQuery } from '../features/parser-import/api/parserImportApi';

const COLUMNS = [
  { key: 'id', label: 'ID' },
  { key: 'url', label: 'URL' },
  { key: 'source', label: 'Source' },
  { key: 'url_available', label: 'URL Available' },
  { key: 'publication_date', label: 'Publication Date' },
  { key: 'title', label: 'Title' },
  { key: 'description', label: 'Description' },
  { key: 'current_price', label: 'Current Price' },
  { key: 'previous_price', label: 'Previous Price' },
  { key: 'location', label: 'Location' },
  { key: 'latitude', label: 'Latitude' },
  { key: 'longitude', label: 'Longitude' },
  { key: 'city', label: 'City' },
  { key: 'country', label: 'Country' },
  { key: 'agency_name', label: 'Agency Name' },
  { key: 'agency_phone', label: 'Agency Phone' },
  { key: 'agency_advert_number', label: 'Agency Advert #' },
  { key: 'living_area', label: 'Living Area' },
  { key: 'land', label: 'Land' },
  { key: 'rooms', label: 'Rooms' },
  { key: 'bedrooms', label: 'Bedrooms' },
  { key: 'baths', label: 'Baths' },
  { key: 'year_built', label: 'Year Built' },
  { key: 'realty_type', label: 'Realty Type' },
  { key: 'language', label: 'Language' },
  { key: 'positionstack_city', label: 'PS City' },
  { key: 'positionstack_location', label: 'PS Location' },
  { key: 'positionstack_region', label: 'PS Region' },
  { key: 'created_at', label: 'Created At' },
  { key: 'updated_at', label: 'Updated At' },
];

export const ParserView = () => {
  const userToken = useSelector(selectUserToken);
  const isAdmin = useSelector(selectIsAdmin);
  const [searchParams, setSearchParams] = useSearchParams();

  const [isImportOpen, setIsImportOpen] = useState(false);
  const openImportFlow = useCallback(() => setIsImportOpen(true), []);
  const closeImportFlow = useCallback(() => setIsImportOpen(false), []);

  const { data: availableCount } = useGetAvailableCountQuery(
    { token: userToken },
    { skip: !userToken || !isAdmin }
  );

  // Display values (immediate, from input fields)
  const [idInput, setIdInput] = useState('');
  const [urlInput, setUrlInput] = useState('');

  // Debounce refs
  const idTimer = useRef(null);
  const urlTimer = useRef(null);

  // Initialize display values from URL params on mount
  useEffect(() => {
    setIdInput(searchParams.get('id') || '');
    setUrlInput(searchParams.get('url') || '');
  }, []);

  // Read current URL params for API call
  const source = searchParams.get('source') || '';
  const id = searchParams.get('id') || '';
  const url = searchParams.get('url') || '';
  const page = parseInt(searchParams.get('page') || '1', 10);
  const perPage = parseInt(searchParams.get('per_page') || '10', 10);
  const sortOrder = searchParams.get('sort_order') || 'desc';
  const sortBy = searchParams.get('sort_by') || 'id';

  const {
    data: parserData,
    isLoading,
    error,
  } = useGetParserDataQuery({
    token: userToken,
    source,
    id,
    url,
    per_page: perPage,
    sort_by: sortBy,
    sort_order: sortOrder,
    page,
  });

  const { data: spidersData } = useGetSpidersQuery({ token: userToken });
  const spiderOptions = (spidersData?.data || []).map(s => ({ title: s, value: s }));

  const handleIdChange = useCallback(e => {
    const value = e.target.value;
    setIdInput(value);
    clearTimeout(idTimer.current);
    idTimer.current = setTimeout(() => {
      const newParams = new URLSearchParams(searchParams);
      newParams.set('id', value);
      newParams.set('page', '1');
      setSearchParams(newParams);
    }, 300);
  }, [searchParams, setSearchParams]);

  const handleUrlChange = useCallback(e => {
    const value = e.target.value;
    setUrlInput(value);
    clearTimeout(urlTimer.current);
    urlTimer.current = setTimeout(() => {
      const newParams = new URLSearchParams(searchParams);
      newParams.set('url', value);
      newParams.set('page', '1');
      setSearchParams(newParams);
    }, 300);
  }, [searchParams, setSearchParams]);

  const handleSortClick = (columnKey) => {
    const newParams = new URLSearchParams(searchParams);
    if (columnKey === sortBy) {
      newParams.set('sort_order', sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      newParams.set('sort_by', columnKey);
      newParams.set('sort_order', 'asc');
    }
    newParams.set('page', '1');
    setSearchParams(newParams);
  };

  const handleNextClick = () => {
    if (parserData?.links?.next) {
      const newParams = new URLSearchParams(searchParams);
      newParams.set('page', String(page + 1));
      setSearchParams(newParams);
    }
  };

  const handlePrevClick = () => {
    if (parserData?.links?.prev) {
      const newParams = new URLSearchParams(searchParams);
      newParams.set('page', String(Math.max(1, page - 1)));
      setSearchParams(newParams);
    }
  };

  // Cleanup timers
  useEffect(() => {
    return () => {
      clearTimeout(idTimer.current);
      clearTimeout(urlTimer.current);
    };
  }, []);

  const records = parserData?.data || [];
  const hasData = records.length > 0;

  return (
    <Container>
      <div className="flex flex-col items-start gap-5 w-full bg-white" style={{ minHeight: 'calc(100vh - 5rem)' }}>
        <div className="flex items-center gap-3 flex-wrap w-full">
          {isAdmin && (
            <span data-testid="parser-import-open-button">
              <ButtonTemplate
                text="Import"
                icon={Upload}
                isIconText={true}
                handleClick={openImportFlow}
                bgColor="bg-purpleColor border-purpleColor"
              />
            </span>
          )}
          {availableCount && (
            <div className="flex items-center gap-2 text-xs">
              <span className="px-2 py-1 rounded bg-purpleColor/10 text-purpleColor font-medium">
                {Number(availableCount.count ?? 0).toLocaleString('fr-FR')} available
              </span>
              {availableCount.new_since_last_import > 0 && (
                <span className="px-2 py-1 rounded bg-greenColor/15 text-greenColor font-medium">
                  +{Number(availableCount.new_since_last_import).toLocaleString('fr-FR')} new
                </span>
              )}
            </div>
          )}
        </div>

        <ParserStats source={source} />

        {/* Filter Bar */}
        <div className="flex flex-wrap items-center gap-3 w-full">
          <Multiselect
            displayValue="title"
            placeholder="Spider"
            options={spiderOptions}
            selectedValues={source ? [{ title: source, value: source }] : []}
            selectionLimit={1}
            showCheckbox={false}
            closeOnSelect={true}
            onSelect={(selectedList) => {
              const value = [...selectedList][0]?.value ?? '';
              const newParams = new URLSearchParams(searchParams);
              if (value) {
                newParams.set('source', value);
              } else {
                newParams.delete('source');
              }
              newParams.set('page', '1');
              setSearchParams(newParams);
            }}
            onRemove={() => {
              const newParams = new URLSearchParams(searchParams);
              newParams.delete('source');
              newParams.set('page', '1');
              setSearchParams(newParams);
            }}
          />
          <input
            type="text"
            placeholder="ID"
            value={idInput}
            onChange={handleIdChange}
            className="px-3 py-2 text-sm border border-gray-200 rounded outline-none focus:border-gray-400"
          />
          <input
            type="text"
            placeholder="URL"
            value={urlInput}
            onChange={handleUrlChange}
            className="px-3 py-2 text-sm border border-gray-200 rounded outline-none focus:border-gray-400"
          />
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="flex justify-center w-full py-8">
            <span className="text-sm text-blackColor">Loading...</span>
          </div>
        )}

        {/* Error State */}
        {error && !isLoading && (
          <div className="flex justify-center w-full py-8">
            <span className="text-sm text-red-500">
              Error loading data: {error.status || 'Unknown error'}
            </span>
          </div>
        )}

        {/* Empty State */}
        {!isLoading && !error && !hasData && (
          <div className="flex justify-center w-full py-8">
            <span className="text-sm text-blackColor">No records found</span>
          </div>
        )}

        {/* Data Table */}
        {!isLoading && !error && hasData && (
          <>
            <div data-testid="table-wrapper" className="w-full flex-1 min-h-0 overflow-auto">
              <table className="min-w-full text-sm border-collapse">
                <thead className="sticky top-0 z-10 bg-gray-50">
                  <tr className="border-b border-gray-200 bg-gray-50">
                    {COLUMNS.map(col => {
                      const isActive = sortBy === col.key;
                      const ariaSortValue = isActive ? (sortOrder === 'asc' ? 'ascending' : 'descending') : undefined;
                      return (
                        <th
                          key={col.key}
                          aria-sort={ariaSortValue}
                          className="px-3 py-2 text-left text-xs font-semibold text-gray-600 whitespace-nowrap"
                        >
                          <button
                            type="button"
                            onClick={() => handleSortClick(col.key)}
                            className="flex items-center gap-1 hover:text-gray-900"
                          >
                            {col.label}
                            {isActive && (
                              <span aria-hidden="true">{sortOrder === 'asc' ? ' \u25b2' : ' \u25bc'}</span>
                            )}
                          </button>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {records.map(row => (
                    <tr
                      key={row.id}
                      className="border-b border-gray-100 hover:bg-gray-50"
                    >
                      {COLUMNS.map(col => (
                        <td
                          key={col.key}
                          className="px-3 py-2 text-sm text-blackColor whitespace-nowrap max-w-[300px] truncate"
                        >
                          {col.key === 'url_available'
                            ? row[col.key]
                              ? 'Yes'
                              : 'No'
                            : row[col.key] ?? '—'}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex justify-end w-full">
              <PaginationComponent
                links={parserData?.links}
                meta={parserData?.meta}
                handleNextClick={handleNextClick}
                handlePrevClick={handlePrevClick}
                selectedItemsPerPage={perPage}
                setSelectedItemsPerPage={(newPerPage) => {
                  setSearchParams(prev => {
                    const newParams = new URLSearchParams(prev);
                    newParams.set('per_page', String(newPerPage));
                    newParams.set('page', '1');
                    return newParams;
                  });
                }}
                setPage={(newPage) => {
                  setSearchParams(prev => {
                    const newParams = new URLSearchParams(prev);
                    newParams.set('page', String(newPage));
                    return newParams;
                  });
                }}
              />
            </div>
          </>
        )}
      </div>

      {isImportOpen && (
        <ImportFlow isOpen={isImportOpen} onClose={closeImportFlow} />
      )}
    </Container>
  );
};
